import React, { useState, useEffect } from 'react';
import { Typography, Notification, Button } from '@telus-uds/components-web';

import { reportsAPI } from '../utils/apiService';
import { getApiErrorMessage } from '../utils/apiErrorMessage';
import DataTable from '../components/DataTable';
import ChartDescription from '../components/ChartDescription';
import LastUpdated from '../components/LastUpdated';
import LoadingSpinner from '../components/LoadingSpinner';
import EmptyState from '../components/EmptyState';

const formatDate = value => (value ? new Date(value).toLocaleString() : 'N/A');

const columns = [
  { key: 'report_type', label: 'Report' },
  { key: 'export_format', label: 'Format' },
  { key: 'status', label: 'Status' },
  { key: 'requested_by', label: 'Requested By' },
  { key: 'created_at', label: 'Date' }
];

const ExportHistoryReport = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [result, setResult] = useState(null);
  // This tab is not served from the report cache, so "last updated" is simply when it was loaded.
  const [loadedAt, setLoadedAt] = useState(null);

  const fetchData = async currentPage => {
    setLoading(true);
    setError(null);
    try {
      const res = await reportsAPI.getExportAuditTrail({ page: currentPage, pageSize: 20 });
      setResult(res.data);
      setLoadedAt(new Date().toISOString());
    } catch (err) {
      setError(getApiErrorMessage(err, 'Failed to fetch export history'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData(page);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  if (loading) {
    return <LoadingSpinner text="Loading export history..." />;
  }

  if (error) {
    return (
      <Notification variant={{ validation: 'error' }}>
        <div>
          <strong>Error:</strong> {error}
        </div>
      </Notification>
    );
  }

  if (!result || result.rows.length === 0) {
    return <EmptyState title="No Exports Yet" description="PDF and Excel report exports will appear here as they happen." />;
  }

  const tableData = result.rows.map((row, index) => ({
    id: index,
    report_type: row.report_type,
    export_format: row.export_format,
    status: row.status,
    requested_by: (row.requested_by && (row.requested_by.name || row.requested_by.email)) || 'Unknown',
    created_at: formatDate(row.created_at)
  }));

  return (
    <div>
      <Typography heading="h2" variant={{ size: 'h2' }} block style={{ color: '#4b286d', marginBottom: '1rem' }}>
        Export History
      </Typography>
      <ChartDescription descriptionKey="exportHistory" />
      <LastUpdated generatedAt={loadedAt} loading={loading} onRefresh={() => fetchData(page)} />
      <DataTable columns={columns} data={tableData} />
      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '1rem'
      }}
      >
        <Button variant={{ priority: 'low' }} disabled={page <= 1} onClick={() => setPage(p => p - 1)}>
          Previous
        </Button>
        <span style={{ fontSize: '0.875rem', color: '#666666' }}>
          Page {result.page} of {result.total_pages} ({result.total} exports)
        </span>
        <Button
          variant={{ priority: 'low' }}
          disabled={page >= result.total_pages}
          onClick={() => setPage(p => p + 1)}
        >
          Next
        </Button>
      </div>
    </div>
  );
};

export default ExportHistoryReport;
